/**
 * render-client.ts — fetch server-rendered audio chunks from the
 * astromusicbackend render service.
 *
 * Production: set VITE_RENDER_API_URL=https://radio.astrogenesis.net/api/v3
 *   (or https://astromusicbackend-production.up.railway.app/api/v3).
 *   NEVER https://api.astrogenesis.net/api/v3 — that host is the astrology
 *   backend and returns 404 on /render/section (P4 routing bug, 2026-09-22).
 * Dev: add a proxy in vite.config.ts  /api/v3 → localhost:8090
 */

// Same-origin /api/v3 via Vite (dev) or nginx (prod) — avoids CSP cross-origin blocks.
const RENDER_API = import.meta.env.VITE_RENDER_API_URL || '/api/v3'

// Only rewrite audio paths when render API is on a different origin.
const RENDER_ORIGIN = (() => {
  try {
    const u = new URL(RENDER_API, typeof window !== 'undefined' ? window.location.origin : 'http://localhost')
    if (typeof window !== 'undefined' && u.origin === window.location.origin) return ''
    return u.origin
  } catch {
    return ''
  }
})()

// ── types ──────────────────────────────────────────────────────────────────

export interface RenderParams {
  ragaName?: string
  sectionId?: 'kriti' | 'alapana' | 'kalpanaswara' | 'tani' | 'tillana' | 'mangalam' | 'radio' | string
  baseHz?: number
  durationSec?: number
  tempoBpm?: number
  seed?: number
  bitrate?: string
  instruments?: (string | { name: string; tuningCents?: number; role?: string; volumeDb?: number })[]
  dashaContext?: Record<string, unknown>
  transitContext?: Record<string, unknown>
  sections?: string[]
  sectionDurations?: Record<string, number>
  sectionRagas?: Record<string, string>
  /** Fields from harmonics plan / suggestedRenderBody */
  giti?: string
  prabandha?: string
  grama?: string
  murchhana?: string
  alankaras?: unknown
  composer?: string
  tala?: string
  chartData?: Record<string, unknown>
  timeline?: Record<string, unknown>
  musicalSignature?: Record<string, unknown>
  sangati?: boolean | number
  sangatiRole?: string
}

export interface RenderResult {
  audioUrl: string
  durationSec: number
  sizeBytes: number
  renderMs: number
  realtimeRatio: number
  cached: boolean
  sectionId?: string
}

// ── core ───────────────────────────────────────────────────────────────────

/**
 * Request a server-rendered audio chunk.
 * Backend caches by sha256(params), so repeat requests return instantly.
 */
const PLAN_KEYS = [
  'giti', 'prabandha', 'grama', 'murchhana', 'alankaras', 'composer', 'tala',
  'chartData', 'timeline', 'musicalSignature', 'sectionRagas', 'sangati', 'sangatiRole',
] as const

export async function requestRender(params: RenderParams): Promise<RenderResult> {
  const body: Record<string, unknown> = {
    ragaName:    params.ragaName    ?? 'Mohanam',
    sectionId:   params.sectionId   ?? 'radio',
    baseHz:      params.baseHz      ?? 220,
    durationSec: params.durationSec ?? 30,
    tempoBpm:    params.tempoBpm    ?? 84,
    seed:        params.seed        ?? Math.floor(Date.now() / 1000),
    bitrate:     params.bitrate     ?? '96k',
  }

  if (Array.isArray(params.instruments) && params.instruments.length > 0) {
    body.instruments = params.instruments
  }
  if (params.dashaContext && typeof params.dashaContext === 'object') {
    body.dashaContext = params.dashaContext
  }
  if (params.transitContext && typeof params.transitContext === 'object') {
    body.transitContext = params.transitContext
  }
  for (const key of PLAN_KEYS) {
    if (params[key] !== undefined) body[key] = params[key]
  }

  const res = await fetch(`${RENDER_API}/render/section`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    const errBody = await res.text().catch(() => '')
    throw new Error(`Render service ${res.status}: ${errBody.slice(0, 200)}`)
  }

  const data = await res.json() as RenderResult

  // Make root-relative paths absolute — render service is on a different origin.
  if (data.audioUrl && RENDER_ORIGIN && data.audioUrl.startsWith('/')) {
    data.audioUrl = RENDER_ORIGIN + data.audioUrl
  }

  return data
}

/** Make an audio resource URL absolute. */
export function audioUrl(audioPath: string | null | undefined): string | null {
  if (!audioPath) return null
  if (RENDER_ORIGIN && audioPath.startsWith('/')) return RENDER_ORIGIN + audioPath
  return audioPath
}

/** Pre-warm the render cache. Fire and forget. */
export function prefetchRender(params: RenderParams): void {
  requestRender(params).catch(() => null)
}

/** Health check — returns true when the render service is reachable. */
export async function isRenderServiceHealthy(): Promise<boolean> {
  try {
    const res = await fetch(`${RENDER_API}/health`, { cache: 'no-store' })
    if (!res.ok) return false
    const j = await res.json() as { ok?: boolean }
    return !!j.ok
  } catch {
    return false
  }
}
